import { captureReactException } from "@sentry/react";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

// Envolve toda a arvore de componentes. Captura qualquer excecao de render
// nao tratada, reporta ao Sentry (com o componentStack, que diz QUAL
// componente quebrou) e mostra uma tela de fallback em vez de uma tela branca.
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Sem DSN configurado, isto e no-op — nao ha overhead nem risco de quebra.
    captureReactException(error, errorInfo);
    console.error("Erro não tratado capturado pelo ErrorBoundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <FallbackScreen onReload={() => window.location.reload()} />;
    }

    return this.props.children;
  }
}

const FallbackScreen = ({ onReload }: { onReload: () => void }) => {
  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full text-center space-y-4">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
          <AlertTriangle className="h-8 w-8 text-red-600" />
        </div>
        <h1 className="text-xl font-semibold text-gray-900">
          Algo deu errado
        </h1>
        <p className="text-sm text-gray-600">
          Ocorreu um erro inesperado. Nossa equipe já foi notificada. Tente
          recarregar a página — se o problema persistir, entre em contato com
          o suporte.
        </p>
        <Button onClick={onReload}>Recarregar página</Button>
      </div>
    </div>
  );
};
